import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { formatPrice } from '../lib/format';
import { isSaleActive, effectivePrice } from '../lib/sale';

const CartPage = () => {
  const { t, getLocalizedText } = useLanguage();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const navigate = useNavigate();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-black py-20 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <div className="w-24 h-24 rounded-full bg-zinc-900 flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="h-12 w-12 text-zinc-600" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">{t.cart.empty}</h1>
          <p className="text-zinc-500 mb-8">{t.cart.continueShopping}</p>
          <Button
            size="lg"
            className="bg-white text-black hover:bg-zinc-200"
            onClick={() => navigate('/hookahs')}
          >
            {t.hero.cta}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        <h1 className="text-4xl font-bold text-white mb-8">{t.cart.title}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.id}
                className="bg-zinc-900 rounded-lg p-4 flex gap-4"
              >
                {/* Image */}
                <Link to={`/product/${item.id}`} className="flex-shrink-0">
                  <div className="w-24 h-24 bg-zinc-800 rounded-lg overflow-hidden">
                    <img
                      src={item.image}
                      alt={getLocalizedText(item.name)}
                      className="w-full h-full object-contain p-2"
                    />
                  </div>
                </Link>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.id}`}>
                    <h3 className="font-medium text-white hover:text-zinc-300 transition-colors line-clamp-2">
                      {getLocalizedText(item.name)}
                    </h3>
                  </Link>
                  {item.brand && (
                    <span className="text-sm text-zinc-500">{item.brand}</span>
                  )}
                  {isSaleActive(item) ? (
                    <p className="font-semibold mt-2">
                      <span className="text-red-500">{formatPrice(item.salePrice)} HUF</span>
                      <span className="text-zinc-500 line-through ml-2 text-sm">{formatPrice(item.price)}</span>
                    </p>
                  ) : (
                    <p className="text-white font-semibold mt-2">
                      {formatPrice(item.price)} HUF
                    </p>
                  )}
                </div>

                {/* Quantity & Actions */}
                <div className="flex flex-col items-end justify-between">
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-zinc-500 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-8 h-8 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-white transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center text-white font-medium">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-8 h-8 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-white transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-zinc-900 rounded-lg p-6 sticky top-24">
              <h2 className="text-xl font-bold text-white mb-6">{t.cart.title}</h2>
              
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-zinc-400 truncate max-w-[150px]">
                      {getLocalizedText(item.name)} x{item.quantity}
                    </span>
                    <span className={isSaleActive(item) ? 'text-red-500' : 'text-white'}>
                      {formatPrice(effectivePrice(item) * item.quantity)} HUF
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-zinc-800 pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold text-white">{t.cart.total}</span>
                  <span className="text-lg font-bold text-white">
                    {formatPrice(getCartTotal())} HUF
                  </span>
                </div>
              </div>

              <Button
                size="lg"
                className="w-full bg-white text-black hover:bg-zinc-200 font-semibold"
                onClick={() => navigate('/checkout')}
              >
                {t.cart.checkout}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                className="w-full mt-4 text-zinc-400 hover:text-white"
                onClick={() => navigate('/hookahs')}
              >
                {t.cart.continueShopping}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
